/**
 * Snowflake Section — org Settings surface for Snowflake connection
 * requests. Any org admin/member can see their org's requests and act on
 * pending ones: approve (opens the approve slideout) or decline.
 */

import { useCallback, useMemo, useState } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { useDisplayTimezone } from '@/hooks/useDisplayTimezone';
import { Button } from '@/lib/base/buttons/button';
import { EmptyState } from '@/lib/base/empty-state/empty-state';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { ConfirmationDialog } from '@/lib/base/overlays/confirmation-dialog';
import { QueryErrorState } from '@/lib/components/QueryErrorState';
import { getSnowflakeActionError } from '@/lib/portfolio/errors';
import { useDeclineSnowflakeRequest } from '@/lib/portfolio/hooks/useDeclineSnowflakeRequest';
import { useOrgSnowflakeRequests } from '@/lib/portfolio/hooks/useOrgSnowflakeRequests';
import type { SnowflakeConnectionRequest } from '@/lib/portfolio/snowflake-types';

import { SnowflakeApproveSlideout } from './SnowflakeApproveSlideout';
import { SnowflakeConnectionManagement } from './SnowflakeConnectionManagement';
import { SnowflakeStatusBadge } from './SnowflakeStatusBadge';

interface SnowflakeRequestRowProps {
  request: SnowflakeConnectionRequest;
  orgId: number;
  onApprove: (requestId: number) => void;
}

function SnowflakeRequestRow({
  request,
  orgId,
  onApprove,
}: SnowflakeRequestRowProps) {
  const [isDeclineDialogOpen, setIsDeclineDialogOpen] = useState(false);
  const declineMutation = useDeclineSnowflakeRequest(orgId);
  const displayTimezone = useDisplayTimezone();

  const handleApproveClick = useCallback(() => {
    onApprove(request.id);
  }, [onApprove, request.id]);

  const handleOpenDeclineDialog = useCallback(() => {
    setIsDeclineDialogOpen(true);
  }, []);

  const handleCloseDeclineDialog = useCallback(() => {
    setIsDeclineDialogOpen(false);
  }, []);

  const handleConfirmDecline = useCallback(async () => {
    await declineMutation.mutateAsync(request.id);
    setIsDeclineDialogOpen(false);
  }, [declineMutation, request.id]);

  const declineError = declineMutation.isError
    ? getSnowflakeActionError(declineMutation.error)
    : null;

  const isPending = request.status === 'pending';

  return (
    <div className='flex flex-col gap-1 py-3'>
      <div className='flex items-center justify-between gap-2'>
        <div className='flex items-center gap-2'>
          <SnowflakeStatusBadge status={request.status} />
          <span className='text-body-small text-[var(--color-text-tertiary)]'>
            {new Date(request.created_at).toLocaleDateString(undefined, {
              timeZone: displayTimezone,
            })}
          </span>
        </div>
        {isPending && (
          <div className='flex items-center gap-2'>
            <Button color='secondary' size='sm' onClick={handleApproveClick}>
              Approve
            </Button>
            <Button
              color='tertiary'
              size='sm'
              isDisabled={declineMutation.isPending}
              isLoading={declineMutation.isPending}
              onClick={handleOpenDeclineDialog}
            >
              Decline
            </Button>
          </div>
        )}
      </div>
      {isPending && (
        <ConfirmationDialog
          isOpen={isDeclineDialogOpen}
          variant='destructive'
          title='Decline this request?'
          confirmLabel='Decline request'
          instance='decline-snowflake-request'
          isLoading={declineMutation.isPending}
          onConfirm={handleConfirmDecline}
          onCancel={handleCloseDeclineDialog}
        >
          {declineError && (
            <p
              role='alert'
              className='text-body-small text-[var(--color-text-error-primary)]'
            >
              {declineError}
            </p>
          )}
        </ConfirmationDialog>
      )}
    </div>
  );
}

interface SnowflakeSectionContentProps {
  orgId: number;
}

function SnowflakeSectionContent({ orgId }: SnowflakeSectionContentProps) {
  const requestsQuery = useOrgSnowflakeRequests(orgId);
  const [approveRequestId, setApproveRequestId] = useState<number | null>(null);

  const handleApprove = useCallback((requestId: number) => {
    setApproveRequestId(requestId);
  }, []);

  const handleApproveSlideoutOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setApproveRequestId(null);
    }
  }, []);

  const { refetch, fetchNextPage } = requestsQuery;
  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const handleLoadMore = useCallback(() => {
    void fetchNextPage();
  }, [fetchNextPage]);

  const requests = useMemo(
    () => requestsQuery.data?.pages.flatMap(page => page.results) ?? [],
    [requestsQuery.data]
  );

  // A failed follow-up page fetch leaves React Query in an error state while
  // `data.pages` still holds the already-loaded results. Scope the footer to
  // `isFetchNextPageError` specifically so a transient pagination failure
  // surfaces a retry for that fetch — and so a failed background refetch (after
  // approve/decline) is NOT mislabeled "Couldn't load more" with a Try again
  // that calls `fetchNextPage` instead of retrying the refetch.
  let listFooter = null;
  if (requestsQuery.isFetchNextPageError) {
    listFooter = (
      <div className='mt-4 flex flex-col items-center gap-2'>
        <p
          role='alert'
          className='text-body-small text-[var(--color-text-error-primary)]'
        >
          Couldn't load more requests.
        </p>
        <Button
          color='secondary'
          size='sm'
          isDisabled={requestsQuery.isFetchingNextPage}
          isLoading={requestsQuery.isFetchingNextPage}
          onClick={handleLoadMore}
        >
          Try again
        </Button>
      </div>
    );
  } else if (requestsQuery.hasNextPage) {
    listFooter = (
      <div className='mt-4 flex justify-center'>
        <Button
          color='secondary'
          size='sm'
          isDisabled={requestsQuery.isFetchingNextPage}
          isLoading={requestsQuery.isFetchingNextPage}
          onClick={handleLoadMore}
        >
          Load more
        </Button>
      </div>
    );
  }

  let body;
  if (requestsQuery.isLoading) {
    body = <LoadingIndicator size='sm' />;
  } else if (requestsQuery.isError && requests.length === 0) {
    body = (
      <QueryErrorState
        error={requestsQuery.error}
        label="Couldn't load Snowflake connection requests"
        onRetry={handleRetry}
      />
    );
  } else if (requests.length === 0) {
    body = (
      <EmptyState variant='empty' title='No Snowflake connection requests' />
    );
  } else {
    body = (
      <>
        <div className='divide-y divide-[var(--color-border-secondary)]'>
          {requests.map(request => (
            <SnowflakeRequestRow
              key={request.id}
              request={request}
              orgId={orgId}
              onApprove={handleApprove}
            />
          ))}
        </div>
        {listFooter}
      </>
    );
  }

  return (
    <div className='mt-4'>
      {body}
      <SnowflakeApproveSlideout
        isOpen={approveRequestId !== null}
        onOpenChange={handleApproveSlideoutOpenChange}
        orgId={orgId}
        requestId={approveRequestId ?? 0}
      />
      <SnowflakeConnectionManagement orgId={orgId} />
    </div>
  );
}

export default function SnowflakeSection() {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;

  return (
    <div>
      <h2 className='text-lg font-semibold text-fg-primary'>Snowflake</h2>
      <p className='text-sm text-fg-tertiary mt-0.5'>
        Review and approve requests to connect this org's data to Snowflake.
      </p>
      {orgId ? (
        // Remount per org: transient state (open approve slideout, selected
        // request id, minted-secret modal) must never carry across an org
        // switch, or an approval could submit against the wrong org's request.
        <SnowflakeSectionContent key={orgId} orgId={orgId} />
      ) : (
        <div className='mt-4'>
          <LoadingIndicator size='sm' />
        </div>
      )}
    </div>
  );
}
